import React, { useState, useEffect } from 'react';
import Reveal from './Reveal';
import WordReveal from './WordReveal';
import Counter from './Counter';
import AnimatedButton from './AnimatedButton';

const InvestorView: React.FC = () => {
  const [investment, setInvestment] = useState(50000);
  const waLink = "https://wa.me/5573998595834?text=Olá,%20vi%20a%20estratégia%20de%20Holding%20Solar%20e%20quero%20começar";

  // Cálculos baseados na taxa de 5.22%
  const monthlyRate = 0.0522;
  const savingsRate = 0.005; // ~0.5% ao mês para poupança

  const solarMonthly = investment * monthlyRate;
  const solar12m = solarMonthly * 12;
  const solar24m = solarMonthly * 24;

  const savingsMonthly = investment * savingsRate;
  const savings12m = savingsMonthly * 12;
  const savings24m = savingsMonthly * 24;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const target = e.target as any;
    const name = target[0].value;
    const contact = target[1].value;
    const message = encodeURIComponent(`Olá, meu nome é ${name}.\nContato: ${contact}\n\nQuero receber o plano de investimento da Holding Solar agora.`);
    window.open(`https://wa.me/5573998595834?text=${message}`, '_blank');
  };

  const steps = [
    { title: "Reserva de Cota", text: "Clique no botão de contato abaixo para iniciar sua aplicação na Holding." },
    { title: "Qualificação", text: "Preencha seus dados no formulário seguro para validar seu perfil de investidor." },
    { title: "Ativação Patrimonial", text: "Em menos de 2 minutos, você receberá o guia para ativar sua unidade geradora e começar a receber." },
  ];

  const benefits = [
    { title: "Juros Compostos Reais", desc: "Com 5,22% ao mês, a \"bola de neve\" do seu patrimônio cresce visivelmente a cada 30 dias." },
    { title: "Blindagem contra a Inflação", desc: "Enquanto o IPCA corrói o poder de compra, sua usina gera energia (que valoriza junto com a tarifa)." },
    { title: "Fim da \"Bancocracia\"", desc: "Esqueça taxas de administração abusivas e produtos de \"gerente\" que só beneficiam a instituição." },
    { title: "Ativo Geracional", desc: "Construa um patrimônio que pode ser herdado, operando por 25 anos com eficiência comprovada." },
    { title: "Eficiência Tributária", desc: "Estrutura fiscal inteligente com assessoria especializada e parecer legal incluso para investidores." },
    { title: "Simplicidade de Holding", desc: "Foco total em um único veículo de alta performance. Nós cuidamos da engenharia, você do lucro." },
    { title: "Dash de Liquidez Visual", desc: "Painel exclusivo para monitorar sua geração diária e histórico de rendimentos." },
  ];

  return (
    <div className="bg-white">
      {/* SECTION: HERO */}
      <section className="pt-48 pb-24 overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto text-center space-y-12">
            <Reveal>
              <div className="inline-block border border-[#39B54A]/20 py-1.5 px-6 rounded-full text-[10px] font-black uppercase tracking-[0.3em] text-[#39B54A] bg-[#39B54A]/5 mb-4">
                Investimento Inteligente
              </div>
            </Reveal>

            <h1 className="text-4xl md:text-7xl font-black text-gray-900 leading-[1.1] tracking-tighter">
              <WordReveal 
                text="Descubra por que os grandes bancos temem que esta 'Estratégia de Holding Solar' desestabilize a Poupança tradicional." 
                delay={200} 
                stagger={60}
                className="justify-center"
              />
            </h1>

            <div className="flex flex-col items-center space-y-6">
              <Reveal delay={1000}>
                <ul className="inline-block text-left space-y-4">
                  <li className="flex items-center space-x-4 text-gray-600 font-bold text-lg">
                    <span className="w-2 h-2 rounded-full bg-[#39B54A]"></span>
                    <span>Como buscar retornos de até 62,6% ao ano ou mais com seu capital protegido por ativos reais.</span>
                  </li>
                  <li className="flex items-center space-x-4 text-gray-600 font-bold text-lg">
                    <span className="w-2 h-2 rounded-full bg-[#39B54A]"></span>
                    <span>Invista como o 1% da elite financeira, sem precisar ser milionário.</span>
                  </li>
                </ul>
              </Reveal>
            </div>

            <Reveal delay={1500}>
              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                <AnimatedButton text="VER ESTRATÉGIA" href={waLink} className="!px-16 !py-6 text-base" />
                <button 
                  onClick={() => document.getElementById('como-comecar')?.scrollIntoView()}
                  className="px-12 py-6 rounded-full border border-gray-200 font-black text-sm text-gray-500 hover:bg-gray-50 transition-colors uppercase tracking-widest bg-white"
                >
                  COMO FUNCIONA
                </button>
              </div>
              <p className="mt-8 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                Solução Desburocratizada (Sem complicações bancárias ou taxas ocultas)
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BARRA DE ESTATÍSTICAS */}
      <section className="py-12 border-y border-gray-100 bg-gray-50/50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { label: "Retorno Anual", val: "62,6%", sub: "Rendimento acumulado" },
              { label: "Dividendos Mensais", val: "5,22%", sub: "Cash-flow recorrente" },
              { label: "Payback Estimado", val: "2.0 Anos", sub: "Retorno do capital" },
              { label: "Performance", val: "12x Maior*", sub: "Que a Poupança" },
            ].map((stat, i) => (
              <Reveal key={i} delay={i * 100} className="text-center">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">{stat.label}</p>
                <p className="text-2xl md:text-3xl font-black text-[#39B54A]">{stat.val}</p>
                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-tighter">{stat.sub}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION: COMO COMEÇAR */}
      <section id="como-comecar" className="py-24">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16 space-y-4">
            <Reveal>
              <h2 className="text-4xl font-black text-gray-900 tracking-tighter">Sua Própria Usina Gerando Renda no Brasil</h2>
              <p className="text-[#39B54A] font-black uppercase tracking-widest text-xs">O Caminho da Renda</p>
              <p className="text-gray-500 font-medium max-w-xl mx-auto mt-4 leading-relaxed">Sem cartório, sem filas, sem burocracia. Processo 100% digital e transparente.</p>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-3 gap-12 max-w-5xl mx-auto">
            {steps.map((step, i) => (
              <Reveal key={i} delay={i * 200}>
                <div className="relative p-10 bg-white border border-gray-100 rounded-[40px] shadow-xl text-center group hover:border-[#39B54A]/30 transition-colors h-full">
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-12 h-12 bg-[#39B54A] rounded-2xl flex items-center justify-center text-white font-black text-xl shadow-lg">
                    {i + 1}
                  </div>
                  <h4 className="text-xl font-black text-gray-900 mb-4 mt-2">
                    {step.title}
                  </h4>
                  <p className="text-gray-500 text-sm font-medium leading-relaxed">
                    {step.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION: SIMULADOR COMPARATIVO */}
      <section className="py-24 bg-gray-950 text-white rounded-[60px] mx-4 md:mx-6 overflow-hidden relative">
        <div className="absolute inset-0 bg-[url('https://i.imgur.com/enHl71N.jpeg')] bg-cover bg-center opacity-10"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16 space-y-4">
              <h2 className="text-4xl md:text-5xl font-black tracking-tighter">
                Simule sua <span className="text-[#39B54A]">Holding de Energia</span>
              </h2>
              <p className="text-gray-400 font-medium max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
                Calcule o poder de multiplicação do seu capital em comparação ao mercado tradicional.
              </p>
            </div>

            {/* Selector de Aporte */}
            <div className="bg-white/5 backdrop-blur-xl p-8 rounded-[32px] border border-white/10 mb-12">
               <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                  <div className="flex-1 w-full">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4 block">Aporte Base do Investidor</label>
                    <input 
                      type="range" 
                      min="50000" 
                      max="300000" 
                      step="50000"
                      value={investment}
                      onChange={(e) => setInvestment(Number(e.target.value))}
                      className="w-full accent-[#39B54A] h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between mt-4 text-[10px] font-black text-gray-500 uppercase">
                      <span>R$ 50k</span>
                      <span>R$ 300k</span>
                    </div>
                  </div>
                  <div className="bg-[#39B54A] px-8 py-4 rounded-2xl text-center min-w-[200px]">
                    <p className="text-[10px] font-black text-white/70 uppercase mb-1">Valor Selecionado</p>
                    <p className="text-2xl font-black text-white">R$ {investment.toLocaleString('pt-BR')}</p>
                  </div>
               </div>
            </div>

            {/* Tabela Comparativa */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-white/10 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    <th className="py-6 text-left px-4">Indicador</th>
                    <th className="py-6 text-center px-4 text-[#39B54A]">Projeção Energy Solaris</th>
                    <th className="py-6 text-center px-4">Poupança Tradicional</th>
                  </tr>
                </thead>
                <tbody className="text-lg">
                  <tr className="border-b border-white/5 group hover:bg-white/5 transition-colors">
                    <td className="py-8 px-4 font-bold text-gray-300">Rendimento Mensal</td>
                    <td className="py-8 px-4 text-center font-black text-white">R$ {Math.floor(solarMonthly).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                    <td className="py-8 px-4 text-center font-bold text-gray-500">~ R$ {Math.floor(savingsMonthly).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                  </tr>
                  <tr className="border-b border-white/5 group hover:bg-white/5 transition-colors">
                    <td className="py-8 px-4 font-bold text-gray-300">Previsibilidade 12 meses</td>
                    <td className="py-8 px-4 text-center font-black text-[#39B54A]">R$ {Math.floor(solar12m).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                    <td className="py-8 px-4 text-center font-bold text-gray-500">~ R$ {Math.floor(savings12m).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                  </tr>
                  <tr className="group hover:bg-white/5 transition-colors">
                    <td className="py-8 px-4 font-bold text-gray-300">Previsibilidade 24 meses</td>
                    <td className="py-8 px-4 text-center font-black text-[#39B54A]">R$ {Math.floor(solar24m).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                    <td className="py-8 px-4 text-center font-bold text-gray-500">~ R$ {Math.floor(savings24m).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-16 p-8 rounded-[32px] border border-white/10 bg-white/5">
              <p className="text-xs text-gray-400 leading-relaxed font-medium">
                <span className="font-black text-white block mb-2 uppercase tracking-widest text-[10px]">Nota Legal:</span>
                Baseado em uma taxa projetada de 5,22% ao mês. Retornos passados não garantem retornos futuros. 
                O Payback de 2.0 anos considera a manutenção da tarifa energética atual. 
                O valor de R$ {investment.toLocaleString('pt-BR')} refere-se ao custo da unidade geradora (ativo físico) que compõe sua Holding.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION: BENEFÍCIOS */}
      <section className="py-32">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-20">
            <Reveal>
              <h2 className="text-4xl md:text-5xl font-black text-gray-900 tracking-tighter mb-6">Por que Energy Solaris?</h2>
              <p className="text-[#39B54A] font-black uppercase tracking-widest text-xs mb-6">Uma nova mentalidade sobre o dinheiro</p>
              <p className="text-gray-500 text-lg font-medium leading-relaxed">
                Técnicas de alavancagem patrimonial antes restritas a grandes fundos institucionais.
              </p>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="p-8 border border-gray-100 rounded-[32px] hover:shadow-2xl transition-all h-full bg-white group">
                  <div className="w-10 h-10 rounded-xl bg-[#39B54A]/10 flex items-center justify-center mb-6 text-[#39B54A] font-black group-hover:bg-[#39B54A] group-hover:text-white transition-colors">
                    {i + 1}
                  </div>
                  <h4 className="text-xl font-black text-gray-900 mb-3">{benefit.title}</h4>
                  <p className="text-gray-500 text-sm font-medium leading-relaxed">{benefit.desc}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={benefits.length * 100}>
                <div className="p-8 bg-[#39B54A]/5 border border-[#39B54A]/20 rounded-[32px] flex flex-col justify-center items-center text-center group h-full">
                  <h4 className="text-2xl font-black text-gray-900 mb-6">Pronto para alavancar?</h4>
                  <AnimatedButton text="MULTIPLICAR MEU CAPITAL" href={waLink} className="w-full !px-8" />
                </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PROVA SOCIAL / DADO DE MERCADO */}
      <section className="py-24 bg-gray-50 relative overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <Reveal>
              <span className="text-[10px] font-black text-[#39B54A] border border-[#39B54A]/20 px-6 py-2.5 rounded-full uppercase tracking-[0.2em] bg-white">DADO CHAVE</span>
            </Reveal>
            <Reveal delay={200}>
              <blockquote className="text-3xl md:text-5xl font-black text-gray-900 leading-[1.15] tracking-tight italic">
                "Apenas 1% dos investidores (~1.000 pessoas no Brasil) estão obtendo em 1 ano no setor de energia o que o cidadão comum levaria 12 anos para conseguir na Renda Fixa tradicional."
              </blockquote>
              <div className="pt-8 flex flex-col items-center">
                <div className="w-12 h-1 bg-[#39B54A] mb-4"></div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em]">
                  – RELATÓRIO SETORIAL DE INVESTIMENTOS ENERGÉTICOS
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CHAMADA PARA AÇÃO FINAL (CTA) */}
      <section id="contato-investidor" className="py-32 bg-white relative overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="relative max-w-6xl mx-auto p-[2.5px] rounded-[3rem] group shadow-2xl overflow-hidden">
            
            {/* Container da Borda Rotativa */}
            <div className="border-animation-container">
              <div className="rotating-border-beam"></div>
            </div>

            {/* Conteúdo do Card (Glass Morphism) */}
            <div className="relative z-10 glass-morphism-solar-light rounded-[3rem] overflow-hidden flex flex-col lg:flex-row min-h-[600px]">
              
              {/* Lado Esquerdo: Identidade Verde */}
              <div className="lg:w-[42%] bg-[#39B54A] p-12 lg:p-20 flex flex-col justify-center relative">
                <div className="absolute inset-0 opacity-10 pointer-events-none">
                  <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <pattern id="grid-investor" width="40" height="40" patternUnits="userSpaceOnUse">
                        <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" />
                      </pattern>
                    </defs>
                    <rect width="100%" height="100%" fill="url(#grid-investor)" />
                  </svg>
                </div>

                <div className="relative z-10 space-y-8">
                  <h3 className="text-4xl lg:text-5xl font-black text-white leading-tight">
                    Veja como é simples começar sua Holding com R$ 50.000 ou até R$ 150.000.
                  </h3>
                  <p className="text-white/90 text-lg font-medium leading-relaxed max-w-md">
                    Não deixe seu capital parado perdendo valor. Permita que o dinheiro que você possui hoje se multiplique exponencialmente, aproveitando o "Ouro do Sol" brasileiro através da tecnologia Energy Solaris.
                  </p>
                  
                  <div className="pt-8 space-y-4">
                    <div className="flex items-center space-x-3">
                      <span className="w-8 h-[2px] bg-white"></span>
                      <span className="text-[10px] font-black text-white uppercase tracking-widest">Sem burocracia bancária.</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="w-8 h-[2px] bg-white"></span>
                      <span className="text-[10px] font-black text-white uppercase tracking-widest">Sem espera de mercado.</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Lado Direito: Formulário */}
              <div className="lg:w-[58%] p-12 lg:p-20 bg-[#F3F4F6]/80 flex flex-col justify-center">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <label className="block text-[10px] font-black text-[#39B54A] uppercase tracking-[0.2em] ml-2">Nome Completo</label>
                    <input 
                      type="text" 
                      required
                      placeholder="Ex: João Silva"
                      className="w-full bg-white border border-gray-200 rounded-2xl px-8 py-5 text-gray-800 placeholder-gray-300 focus:outline-none focus:border-[#39B54A] transition-all font-bold shadow-sm"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-[10px] font-black text-[#39B54A] uppercase tracking-[0.2em] ml-2">WhatsApp / E-mail</label>
                    <input 
                      type="text" 
                      required
                      placeholder="Ex: (73) 99999-9999 ou seu@email.com"
                      className="w-full bg-white border border-gray-200 rounded-2xl px-8 py-5 text-gray-800 placeholder-gray-300 focus:outline-none focus:border-[#39B54A] transition-all font-bold shadow-sm"
                    />
                  </div>
                  
                  <div className="pt-8">
                    <AnimatedButton 
                      text="RECEBER PLANO DE INVESTIMENTO AGORA" 
                      type="submit"
                      className="w-full !rounded-2xl !py-7 shadow-xl"
                    />
                  </div>

                  <p className="text-center text-gray-400 text-[9px] font-bold tracking-tight mt-6 leading-relaxed">
                    Aviso de Risco: O investimento em ativos reais de energia envolve riscos de mercado e operacionais. 
                    Leia o descritivo de viabilidade técnica antes de realizar seu aporte.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER INVESTOR SPECIFIC */}
      <footer className="py-12 text-center border-t border-gray-100">
          <p className="text-[10px] font-black text-gray-300 uppercase tracking-[0.3em]">
            ENERGY SOLARIS © 2026. Todos os direitos reservados.
          </p>
      </footer>
    </div>
  );
};

export default InvestorView;